import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import SoilHealthCard from "../components/SoilHealthCard";
import WaterSavedCard from "../components/WaterSavedCard";
import SustainabilityScoreCard from "../components/SustainabilityScoreCard";
import CarbonCreditCard from "../components/CarbonCreditCard";
import DeviceLocationCard from "../components/DeviceLocationMapCard";

const Dashboard = () => {
  const { accessToken } = useAuth();
  const { userData } = useOutletContext();
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState("all");
  const [loadingSites, setLoadingSites] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const baseURL = process.env.REACT_APP_BASEURL;

  // Fetch sites
  useEffect(() => {
    const fetchSites = async () => {
      setLoadingSites(true);
      setError(null);
      try {
        const siteRes = await axios.get(`${baseURL}/get-sites`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const fetchedSites = siteRes.data.data || [];
        setSites(fetchedSites);
        setLastUpdated(new Date());
      } catch (error) {
        console.error(
          "Sites fetch error:",
          error.response ? error.response.data : error.message
        );
        setError("Failed to load sites. Please try again later.");
      } finally {
        setLoadingSites(false);
      }
    };
    fetchSites();
  }, [accessToken, baseURL]);

  // Update lastUpdated when selectedSiteId changes
  useEffect(() => {
    if (!loadingSites && !error) {
      setLastUpdated(new Date());
    }
  }, [selectedSiteId, loadingSites, error]);

  const handleSiteChange = (event) => {
    setSelectedSiteId(event.target.value);
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (!userData) return "Loading...";
    const fullName = `${userData.first_name || ""} ${userData.last_name || ""}`.trim();
    return fullName || userData.username || "User";
  };

  if (error) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 0, lg: 0 } }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const selectedSite = sites.find((site) => site.id === selectedSiteId);

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 0, lg: 0 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          Welcome, {getUserDisplayName()}
          <Typography variant="body2" sx={{ mb: 2 }}>
            {selectedSiteId === "all"
              ? "Overview of all sites"
              : `Site: ${selectedSite?.name || "Loading..."}`}
          </Typography>
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>
          <FormControl sx={{ width: 250 }}>
            <InputLabel id="site-select-label">Select Site</InputLabel>
            <Select
              labelId="site-select-label"
              value={selectedSiteId}
              label="Select Site"
              onChange={handleSiteChange}
            >
              <MenuItem value="all">All Sites</MenuItem>
              {sites.map((site) => (
                <MenuItem key={site.id} value={site.id}>
                  {site.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", opacity: 0.7 }}
          >
            {loadingSites
              ? "Updating..."
              : lastUpdated
              ? `Last updated: ${lastUpdated.toLocaleString()}`
              : "No updates yet"}
          </Typography>
        </Box>
      </Box>

      {/* Cards Section */}
      <Grid
        container
        spacing={2}
        columnSpacing={4}
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "center",
        }}
      >
        {/* First Column */}
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <Grid container direction="column" spacing={2} rowSpacing={4}>
            <Grid>
              <SoilHealthCard siteId={selectedSiteId} />
            </Grid>
            <Grid>
              <CarbonCreditCard siteId={selectedSiteId} />
            </Grid>
          </Grid>
        </Grid>

        {/* Second Column */}
        <Grid size={{ xs: 12, md: 6, lg: 8 }}>
          <Grid container direction="column" spacing={2}>
            <Grid sx={{ width: "100%" }}>
              <Grid container spacing={4} rowSpacing={2.25}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <WaterSavedCard siteId={selectedSiteId} userSites={userData?.sites} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <SustainabilityScoreCard siteId={selectedSiteId} />
                </Grid>
              </Grid>
            </Grid>
            <Grid>
              <DeviceLocationCard siteId={selectedSiteId} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
